import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ServiceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, translations, language } = useLanguage();

  // Get service data from translations based on slug
  const getServiceBySlug = (slug) => {
    const servicePages = translations[language]?.servicePages;
    if (!servicePages) return null;
    
    for (const key of Object.keys(servicePages)) {
      if (servicePages[key].slug === slug) {
        return servicePages[key];
      }
    }
    return null;
  };

  const service = getServiceBySlug(slug);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="min-h-screen flex items-center justify-center pt-20">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('serviceDetail.notFound')}</h1>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-yellow-400 text-gray-900 px-6 py-3 rounded-full font-bold hover:bg-yellow-500 transition-all"
            >
              <ArrowLeft className="h-5 w-5" />
              {t('serviceDetail.backToHome')}
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={service.heroImage}
            alt={service.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-yellow-500 opacity-90"></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 pt-8">
          <Link
            to="/"
            data-testid="back-to-home-btn"
            className="inline-flex items-center gap-2 text-gray-900 bg-white/80 hover:bg-white px-4 py-2 rounded-lg transition-all duration-300 mb-8 backdrop-blur-sm border border-white/30 cursor-pointer hover:scale-105"
            style={{ position: 'relative', zIndex: 60 }}
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="font-semibold">{t('serviceDetail.backToHome')}</span>
          </Link>

          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
              {service.title}
            </h1>
            <p className="text-xl md:text-2xl text-white/95 mb-6">
              {service.subtitle}
            </p>
            <p className="text-lg text-white/90 leading-relaxed">
              {service.description}
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            {t('serviceDetail.benefits')}
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {service.benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
              >
                <div className="w-14 h-14 rounded-2xl bg-yellow-400 flex items-center justify-center mb-6">
                  <Check className="h-7 w-7 text-gray-900" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services List Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            {t('serviceDetail.serviceDetails')}
          </h2>
          <div className="max-w-3xl mx-auto">
            <ul className="space-y-4">
              {service.services.map((item, index) => (
                <li
                  key={index}
                  className="flex items-start space-x-4 p-6 bg-white rounded-xl hover:shadow-lg transition-all duration-300 border border-gray-100"
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-yellow-400 flex items-center justify-center mt-1">
                    <span className="text-gray-900 font-bold text-sm">{index + 1}</span>
                  </div>
                  <p className="text-gray-800 text-lg leading-relaxed">{item}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            {t('serviceDetail.howWeWork')}
          </h2>
          <div className="space-y-8">
            {service.process.map((step, index) => (
              <div
                key={index}
                className="flex flex-col md:flex-row items-center gap-8 group"
              >
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 rounded-full bg-yellow-400 flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-all duration-300">
                    <span className="text-4xl font-bold text-gray-900">{step.step}</span>
                  </div>
                </div>
                <div className="flex-1 bg-gray-50 rounded-2xl p-8 shadow-lg group-hover:shadow-2xl transition-all duration-300 border border-gray-100">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-yellow-600 transition-colors">
                    {step.title}
                  </h3>
                  <p className="text-gray-700 text-lg leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
            {t('serviceDetail.ctaTitle')}
          </h2>
          <p className="text-xl text-gray-800 mb-10">
            {service.title} {t('serviceDetail.ctaDescription')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={scrollToContact}
              data-testid="service-get-quote-btn"
              className="bg-gray-900 text-white hover:bg-gray-800 text-lg px-8 py-6 rounded-full font-bold shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              {t('serviceDetail.getQuote')}
            </Button>
            <Link
              to="/"
              data-testid="other-services-btn"
              className="inline-flex items-center justify-center bg-white border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white text-lg px-8 py-3 rounded-full font-bold shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              {t('serviceDetail.otherServices')}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceDetail;
